import { useCallback, useEffect, useState } from 'react';

import { ARROW_LEFT, ARROW_RIGHT } from 'approot/shared/tabs/tabs.constants';

export function usePagesPreview<T extends { id: any }>({
  items,
  selected,
  captureKeypressNavigation,
}: {
  items: T[];
  selected?: T;
  captureKeypressNavigation: boolean;
}) {
  const initialItem = items.findIndex(cmp => cmp.id === selected?.id);
  const [index, setIndex] = useState<number>(
    initialItem === -1 ? 0 : initialItem
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getPrev = useCallback(() => {
    if (index - 1 >= 0) {
      setIsLoading(true);
      setIndex(index - 1);
    }
  }, [index, setIsLoading]);

  const getNext = useCallback(() => {
    if (index + 1 < items.length) {
      setIsLoading(true);
      setIndex(index + 1);
    }
  }, [items, index, setIsLoading]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ARROW_LEFT) {
        e.preventDefault();
        e.stopPropagation();
        getPrev();
      }

      if (e.key === ARROW_RIGHT) {
        e.preventDefault();
        e.stopPropagation();
        getNext();
      }
    },
    [getPrev, getNext]
  );

  useEffect(() => {
    if (captureKeypressNavigation) {
      document.addEventListener('keydown', handleKeyDown, true);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [captureKeypressNavigation, handleKeyDown]);

  return {
    index,
    isLoading,
    getPrev,
    getNext,
    setIndex,
    setIsLoading,
  };
}

export function usePageSeriesNavigation({
  totalPages = 1,
  initialIndex,
  captureKeypressNavigation,
}: {
  totalPages?: number;
  initialIndex?: number;
  captureKeypressNavigation: boolean;
}) {
  const [index, setIndex] = useState<number>(initialIndex || 0);

  const getPrev = useCallback(() => {
    if (index - 1 >= 0) {
      setIndex(index - 1);
    }
  }, [index]);

  const getNext = useCallback(() => {
    if (index + 1 < totalPages) {
      setIndex(index + 1);
    }
  }, [totalPages, index]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ARROW_LEFT) {
        e.preventDefault();
        e.stopPropagation();
        getPrev();
      }

      if (e.key === ARROW_RIGHT) {
        e.preventDefault();
        e.stopPropagation();
        getNext();
      }
    },
    [getPrev, getNext]
  );

  useEffect(() => {
    if (captureKeypressNavigation) {
      document.addEventListener('keydown', handleKeyDown, true);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [captureKeypressNavigation, handleKeyDown]);

  return {
    index,
    getPrev,
    getNext,
    setIndex,
  };
}
